import { convertISODate } from "@entrilia/shared/utilities/dateUtils";
import type { Maybe } from "@entrilia/shared/types";
import type { AssistantTask, AssistantTaskSkipReason } from "../../../../api/types/assistantTypes";

const skipReasonTexts: Record<AssistantTaskSkipReason, string> = {
  Missed: "it couldn't start on time",
  CouldNotStart: "it couldn't start",
  StillRunning: "the previous run was still working",
};

const getSkipReasonText = (reason: AssistantTaskSkipReason | undefined): string =>
  reason ? skipReasonTexts[reason] : "it couldn't start";

export const formatTaskTime = (isoDateString: Maybe<string>): string => convertISODate(isoDateString, "MMM d, p");

export const getAttentionTitle = (task: AssistantTask): string => {
  const problemAt = formatTaskTime(task.problemAt);
  switch (task.attentionReason) {
    case "PausedNoAccess":
      return "Paused — you no longer have access to what it needs";
    case "PausedFailures":
      return "Paused after repeated failed runs";
    case "Skipped":
      return `The ${problemAt} run was skipped — ${getSkipReasonText(task.lastSkipReason)}`;
    default:
      return `The last run failed — ${problemAt}`;
  }
};

export const getTaskOutcomeLine = (task: AssistantTask): string => {
  switch (task.attentionReason) {
    case "PausedNoAccess":
      return "Paused — access lost";
    case "PausedFailures":
      return "Paused after repeated failed runs";
    case "Skipped":
      return `Skipped · ${formatTaskTime(task.problemAt)}`;
    case "LastRunFailed":
      return `Failed · ${formatTaskTime(task.problemAt)}`;
  }

  const finished = formatTaskTime(task.lastRunFinishedAt);
  if (task.status === "Finished") {
    return `${finished} · finished, won't run again`;
  }

  return task.nextRunAt ? `${finished} · next ${formatTaskTime(task.nextRunAt)}` : finished;
};

export const getScheduledTaskLine = (task: AssistantTask): string => {
  if (task.status === "Paused") {
    return "Paused by you";
  }

  return task.nextRunAt ? `Next ${formatTaskTime(task.nextRunAt)}` : "Not scheduled";
};
